import { INTRO_FRAMES } from "../IntroClip";
import type { ContentRepository } from "../content/repository";
import type { DialogueId, LevelId } from "../content/types";
import { CONTENT_SCHEMA_VERSION } from "../content/types";
import {
  Event,
  Key,
  MovieClip,
  Sprite,
  currentStage,
  type PointerInput,
} from "../runtime";
import { CutSceneSignal, GuiAction } from "./actions";
import { whiteBlock } from "./assets";
import { ConfirmBox } from "./ConfirmBox";
import { KEY_BINDINGS, NO_INPUT } from "./data/input";
import type { SceneName } from "./data/scenes";
import { selectableMapNodes } from "./data/worldMap";
import { CutScene } from "./CutScene";
import { FadeIn } from "./FadeIn";
import { Hud } from "./Hud";
import { LevelRunner } from "./LevelRunner";
import type { LevelSession } from "./LevelRunner";
import { Menus } from "./Menus";
import { newSaveState, SaveFile } from "./SaveFile";
import type { ItemSlot, SaveState } from "./SaveFile";
import { SoundFX } from "./SoundFX";
import { UnlockBox } from "./UnlockBox";

/**
 * Stand-in for the `null` the AS3 wrote into typed display-object slots when a
 * scene was torn down. `never` lands in every field without loosening the
 * declared types; nothing reads a slot before the next scene rebuilds it.
 */
const RELEASED = null as unknown as never;

/** Cutscene signals that request a sound from the game. */
const CUTSCENE_SOUNDS: Readonly<
  Partial<Record<CutSceneSignal, (fx: SoundFX) => void>>
> = {
  [CutSceneSignal.ThunderFlash]: (fx) => fx.thunder(),
  [CutSceneSignal.BlueprintBuilt]: (fx) => fx.openingComputer(),
  [CutSceneSignal.RobotLaunched]: (fx) => fx.openingBoost(),
  [CutSceneSignal.RobotShine]: (fx) => fx.shine(),
  [CutSceneSignal.FortressAppeared]: (fx) => fx.appear(),
  [CutSceneSignal.FortressDestroyed]: (fx) => fx.explodeGargantuan(),
};

export class Game extends MovieClip {
  private sceneType!: SceneName;
  private gui!: Hud;
  private fader!: FadeIn;
  private menu!: Menus;
  private uBox!: UnlockBox;
  private cBox!: ConfirmBox;
  private cs!: CutScene;
  private levelRunner!: LevelRunner;
  private levelSession!: LevelSession;
  private level: LevelId;
  private soundFX!: SoundFX;
  private input = NO_INPUT.slice();
  private flasher!: MovieClip;
  private flashAlpha = 0;
  private flashTotal = 0;
  private readonly saveFile: SaveFile;
  private highScores: Record<LevelId, number> = {};
  private itemData: Record<LevelId, ItemSlot> = {};
  private itemFound: { weaponChip: number; armourChip: number } = {
    weaponChip: 0,
    armourChip: 0,
  };
  private weaponData: number[] = [2, 2, 2, 2, 2];
  private soundState = 1;
  private hyperArmour = false;
  private savedProgress = 1;
  private savedWeapon = 0;
  private unlockedLevels: LevelId[] = [];
  private completedLevels: LevelId[] = [];
  private acknowledgedLevels: LevelId[] = [];
  private levelSelected: LevelId;
  private eventStack: DialogueId[] = [];
  private gotoScene: SceneName = "level select";
  private introCounter = 0;
  private introRunning = false;
  private introSkipped = false;
  private cheatsOn = false;
  /** Console unlock: enables the cheat menu until the page is reloaded. */
  private cheatsSessionUnlock = false;
  private menuRefreshPending = false;

  constructor(
    readonly content: ContentRepository,
    private readonly pointerInput: PointerInput,
  ) {
    super();
    const initialLevel = content.initialLevelIds[0];
    if (initialLevel == null) throw new Error("content has no initial level");
    this.level = initialLevel;
    this.levelSelected = initialLevel;
    this.saveFile = new SaveFile(content);
    this.totalFrames = 4;
    this.stop();
    this.addEventListener(Event.ENTER_FRAME, this.loading2);
    Key.initialize(this.stage);
  }

  /** Routes a canvas press to the interactive control at that stage point. */
  pointerDown(stageX: number, stageY: number): void {
    if (this.introRunning) {
      this.introSkipped = true;
      return;
    }
    switch (this.sceneType) {
      case "opening":
      case "ending":
        this.pointerInput.press(4);
        break;
      case "title":
        if (this.uBox != null) {
          this.pointerInput.press(4);
        } else {
          this.menu.pointerDown(stageX, stageY);
        }
        break;
      case "main menu":
        if (this.cBox != null) {
          if (this.cBox.pointerDown(stageX, stageY)) {
            this.pointerInput.press(4);
          }
        } else {
          this.menu.pointerDown(stageX, stageY);
        }
        break;
      case "level select":
        if (this.gui.eventObject != null) {
          this.pointerInput.press(4);
        } else if (this.gui.movingBar.length === 0) {
          this.menu.pointerDown(stageX, stageY);
        }
        break;
      case "level": {
        const slot = this.levelRunner.pointerInputAt(stageX, stageY);
        if (slot != null) this.pointerInput.press(slot);
        break;
      }
    }
  }

  /**
   * Console hook: reveals the main-menu cheat toggle for this page load only.
   * Nothing is written to the save slot, so a reload takes the button away
   * again unless the campaign is genuinely finished.
   */
  unlockCheatsForSession(): void {
    this.cheatsSessionUnlock = true;
    this.menuRefreshPending = true;
  }

  private loading2 = (): void => {
    if (this.currentFrame === 2) {
      this.removeEventListener(Event.ENTER_FRAME, this.loading2);
      this.addEventListener(Event.ENTER_FRAME, this.maxIntro);
      this.introRunning = true;
    }
  };

  /** Holds the intro card for its window, or until any key or pointer press. */
  private maxIntro = (): void => {
    ++this.introCounter;
    if (
      this.introCounter < INTRO_FRAMES &&
      !this.introSkipped &&
      !Key.anyDown()
    ) {
      return;
    }
    this.introCounter = 0;
    this.introRunning = false;
    this.removeEventListener(Event.ENTER_FRAME, this.maxIntro);
    this.startGame();
  };

  private startGame(): void {
    currentStage().frameRate = 50;
    this.addEventListener("enterFrame", this.update);
    this.wipeData();
    if (this.saveFile.isEmpty) {
      this.saveData();
    } else {
      this.loadData();
    }
    this.gotoAndStop(3);
    this.sceneSetup("opening");
  }

  // ------------------------------------------------------------- scenes

  private sceneSetup(command: SceneName): void {
    this.soundFX = new SoundFX();
    this.soundFX.updateSoundState(this.soundState);
    this.sceneType = command;
    switch (this.sceneType) {
      case "opening":
        this.cs = new CutScene(0, this.hyperArmour);
        this.addChild(this.cs);
        break;
      case "ending":
        this.soundFX.musicStart(16);
        this.cs = new CutScene(1, this.hyperArmour);
        this.addChild(this.cs);
        break;
      case "title":
      case "main menu":
        this.setupMenuScene();
        break;
      case "level select":
        this.setupLevelSelectScene();
        break;
      case "level":
        this.setupLevelScene();
        break;
    }
    this.startFade(false, this.sceneType === "title");
  }

  private setupMenuScene(): void {
    this.addMenu();
    if (this.sceneType !== "title") return;
    this.flasherEvents(0);
    // each milestone shows its unlock popup exactly once, then banks progress
    const unlock = this.content.worldMap.milestoneUnlocks.find(
      (candidate) => candidate.at === this.savedProgress,
    );
    if (unlock == null) return;
    this.flasherEvents(1, 2);
    this.savedProgress = unlock.next;
    if (unlock.unlockLevel !== undefined) {
      this.unlockLevel(unlock.unlockLevel);
    }
    this.uBox = new UnlockBox(unlock.message);
    this.addChild(this.uBox);
    this.soundFX.effect2();
    this.saveData();
  }

  private setupLevelSelectScene(): void {
    const acknowledged = new Set(this.acknowledgedLevels);
    const newlyUnlocked = selectableMapNodes(
      this.content.worldMap,
      this.unlockedLevels,
    ).filter((node) => !acknowledged.has(node.id));
    const unlockedNode = newlyUnlocked[0];
    const newLevelUnlocked = unlockedNode !== undefined;
    if (unlockedNode !== undefined) {
      this.levelSelected = unlockedNode.id;
      this.acknowledgedLevels = [...this.acknowledgedLevels, unlockedNode.id];
    }
    const displayLevel = this.content.hasNode(this.levelSelected)
      ? this.content.node(this.levelSelected).label
      : "";
    this.gui = new Hud(
      this.sceneType,
      displayLevel,
      this.weaponData,
      this.hyperArmour,
      (id) => this.content.dialogue(null, id),
    );
    this.addMenu();
    if (this.itemFound.armourChip > 0) {
      this.menu.chipDataArray.push({ id: 0, qu: this.itemFound.armourChip });
    }
    if (this.itemFound.weaponChip > 0) {
      this.menu.chipDataArray.push({ id: 1, qu: this.itemFound.weaponChip });
    }
    this.menu.setChipData();
    this.addChild(this.gui);
    if (newLevelUnlocked) {
      this.gui.setupMissionBar(3);
      this.saveData();
    }
    this.updateItemText();
    this.gui.highScoreLabel.updateText(
      String(this.highScores[this.menu.levelSelected] ?? 0),
    );
    this.soundFX.musicStart(20);
  }

  private addMenu(): void {
    this.menu = new Menus(
      this.content,
      this.sceneType,
      this.savedWeapon,
      this.unlockedLevels,
      this.levelSelected,
      this.soundState,
      this.hyperArmour,
      this.savedProgress,
      this.highScores[this.content.survival.id] ?? 0,
      { unlocked: this.cheatsUnlocked, on: this.cheatsOn },
    );
    this.addChild(this.menu);
  }

  private setupLevelScene(): void {
    const session: LevelSession = {
      levelId: this.level,
      selectedWeapon: this.savedWeapon,
      unlockedLevels: this.unlockedLevels,
      highScores: this.highScores,
      itemData: this.itemData,
      itemFound: this.itemFound,
      weaponData: this.weaponData,
      savedProgress: this.savedProgress,
      hyperArmour: this.hyperArmour,
      eventStack: this.eventStack,
      cheats: this.cheatsUnlocked && this.cheatsOn,
    };
    this.levelSession = session;
    this.levelRunner = new LevelRunner(this.content, session, this.soundFX, {
      save: () => {
        this.applyLevelSession(session);
        this.saveData();
      },
      completeLevel: (levelId, score) => {
        this.applyLevelSession(session);
        this.completeLevel(levelId, score);
      },
      requestScene: (scene, colored = false, slowFade = false) => {
        this.gotoScene = scene;
        this.startFade(true, colored, slowFade);
      },
    });
    this.savedWeapon = session.selectedWeapon;
    this.addChild(this.levelRunner);
  }

  private startFade(fadingIn = false, colour = false, slowFade = false): void {
    this.fader = new FadeIn(fadingIn, colour, slowFade);
    if (this.sceneType === "level") {
      this.levelRunner.addChild(this.fader);
    } else {
      this.addChild(this.fader);
    }
  }

  // ------------------------------------------------------------- main loop

  private update = (): void => {
    this.keyEvents();
    this.refreshMenuIfPending();
    if (this.sceneType === "level") {
      this.levelRunner.update(this.input);
    } else if (this.sceneType === "opening" || this.sceneType === "ending") {
      this.updateCutScene();
    } else {
      this.updateMenuScene();
    }
    this.updateFader();
    this.flasherEvents(2);
  };

  /**
   * Rebuilds the main menu in place once the console unlock lands, so the
   * cheat toggle shows up without leaving the screen. Waits for any fade or
   * dialog to clear first, since both sit above the menu in the child list.
   */
  private refreshMenuIfPending(): void {
    if (!this.menuRefreshPending) return;
    if (this.sceneType !== "main menu") return;
    if (this.fader != null || this.cBox != null) return;
    this.menuRefreshPending = false;
    this.removeChild(this.menu);
    this.addMenu();
    this.menu.guiState = 0;
  }

  private updateCutScene(): void {
    const csAction = (this.cs.update(this.input[4]) | 0) as CutSceneSignal;
    CUTSCENE_SOUNDS[csAction]?.(this.soundFX);
    switch (csAction) {
      case CutSceneSignal.StartEndingMusic:
        this.soundFX.musicStart(15);
        break;
      case CutSceneSignal.SceneFinished:
        this.gotoScene = "title";
        this.startFade(true, true);
        break;
      case CutSceneSignal.OpeningSkipped:
        this.gotoScene = "title";
        this.startFade(true, true);
        this.soundFX.musicFadeOut(0.05);
        break;
      case CutSceneSignal.EndingFinished:
      case CutSceneSignal.EndingSkipped:
        this.gotoScene = "title";
        if (csAction === CutSceneSignal.EndingFinished) {
          this.startFade(true, true, true);
          this.soundFX.musicFadeOut(0.02);
        } else {
          this.startFade(true, true);
          this.soundFX.musicFadeOut(0.05);
        }
        if (this.savedProgress < 5) {
          this.savedProgress = 5;
          this.saveData();
        }
        break;
    }
  }

  private updateMenuScene(): void {
    if (
      this.sceneType !== "title" &&
      this.sceneType !== "main menu" &&
      this.sceneType !== "level select"
    ) {
      return;
    }
    let menuInput = this.input;
    if (this.sceneType === "title" && this.uBox != null) {
      menuInput = [];
      if (this.uBox.update(this.input[4])) {
        this.removeChild(this.uBox);
        this.uBox = RELEASED;
      }
    }
    if (this.sceneType === "main menu" && this.cBox != null) {
      menuInput = [];
      this.updateConfirmBox();
    }
    if (this.sceneType === "level select") {
      const busy =
        this.gui.eventObject != null || this.gui.movingBar.length > 0;
      if (busy) {
        menuInput = [false, false, false, false, false];
      } else if (this.eventStack.length > 0) {
        this.gui.newEvent(this.eventStack[0]);
        this.eventStack.splice(0, 1);
      }
    }

    const r = this.menu.update(menuInput);
    if (r.action === 1) {
      if (r.goto !== "") {
        // Menus reports the destination as a free string; the set it can emit
        // is exactly SceneName
        this.gotoScene = r.goto as SceneName;
        if (this.gotoScene === "level" && r.extraValue != null) {
          this.level = r.extraValue;
          this.levelSelected = r.extraValue;
        }
        if (this.sceneType === "level select") {
          this.soundFX.musicFadeOut(0.05);
        }
        this.startFade(true, false);
      }
      if (r.gotoAction === 1) {
        this.menu.guiState = 0;
        this.cBox = new ConfirmBox();
        this.addChild(this.cBox);
      }
    }
    if (this.sceneType === "title" || this.sceneType === "main menu") {
      this.menu.titleEvents();
      if (this.sceneType === "main menu" && r.action === 2) {
        this.soundState = this.menu.soundState;
        this.cheatsOn = this.menu.cheatsOn;
        this.soundFX.updateSoundState(this.soundState);
        this.saveData();
      }
    }
    if (this.sceneType === "level select") {
      this.updateLevelSelectLabels(r.action);
      this.updateLevelSelectGui();
    }
    if (r.soundAction === 1) this.soundFX.menuChange1();
    if (r.soundAction === 2) this.soundFX.menuChange2();
    if (r.soundAction === 3) this.soundFX.menuSelect1();
  }

  private updateConfirmBox(): void {
    const cBoxAction = this.cBox.update(this.input) | 0;
    if (cBoxAction === 1) {
      if (this.cBox.boxSelected) {
        this.removeChild(this.cBox);
        this.cBox = RELEASED;
      } else {
        this.wipeData();
        this.saveData();
        this.gotoScene = "opening";
        this.startFade(true, false);
      }
    }
    if (cBoxAction === 2) this.soundFX.menuChange1();
    if (cBoxAction === 3) this.soundFX.menuSelect1();
  }

  private updateLevelSelectLabels(action: number): void {
    switch (action) {
      case 2:
        this.gui.highScoreLabel.updateText(
          String(this.highScores[this.menu.levelSelected] ?? 0),
        );
        this.updateItemText();
        break;
      case 10:
        this.gui.currentOptionLabel.updateText("MISSION SELECT");
        break;
      case 11:
        this.gui.currentOptionLabel.updateText("WWW.MAXGAMES.COM");
        break;
      case 12:
        this.gui.currentOptionLabel.updateText("RETURN TO MAIN MENU");
        break;
    }
  }

  private updateLevelSelectGui(): void {
    this.gui.update(this.input);
    if (this.gui.soundAction > 0) {
      if (this.gui.soundAction === 1) {
        this.soundFX.textBoxAppear();
      }
      this.gui.soundAction = 0;
    }
    if (this.gui.action > 0) {
      if (this.gui.action === GuiAction.UpgradeArmourPreview) {
        this.menu.knight!.upgradeArmour();
      }
      this.gui.action = GuiAction.None;
    }
  }

  private updateFader(): void {
    if (this.fader == null) return;
    const faderAction = this.fader.update() | 0;
    if (faderAction === 1) {
      if (this.sceneType === "level") {
        this.levelRunner.removeChild(this.fader);
      } else {
        this.removeChild(this.fader);
      }
      this.fader = RELEASED;
      if (this.menu != null) {
        this.menu.guiState = 0;
      }
    }
    if (faderAction !== 2) return;
    if (this.gotoScene === "level select") {
      // the VR tutorial has to be cleared before the mission list opens
      if (this.savedProgress < 2) {
        this.gotoScene = "level";
        this.level = this.content.tutorial.id;
      }
      // the original carries the current weapon forward only through mission select
      if (this.levelRunner != null) {
        this.savedWeapon = this.levelRunner.selectedWeapon;
      }
    }
    if (this.sceneType === "level select") {
      this.levelSelected = this.menu.levelSelected;
    }
    if (this.gotoScene === "survival") {
      this.level = this.content.survival.id;
      this.levelSelected = this.level;
      this.gotoScene = "level";
    }
    this.destroyAll();
    this.sceneSetup(this.gotoScene);
  }

  private updateItemText(): void {
    const item = this.itemData[this.menu.levelSelected];
    let itemText = "";
    if (item?.id > 0) {
      itemText =
        item.found > 0 ? "HIDDEN ITEM LOCATED!" : "HIDDEN ITEM NOT YET FOUND";
    }
    this.gui.itemFoundLabel.updateText(itemText);
  }

  private flasherEvents(mode = 1, flashTotal = 1): void {
    if (mode === 0) {
      this.flasher = new MovieClip();
      this.addChild(this.flasher);
      const whiteFill = new Sprite();
      whiteFill.graphics.beginBitmapFill(new whiteBlock());
      whiteFill.graphics.drawRect(0, 0, 720, 480);
      whiteFill.graphics.endFill();
      this.flasher.addChild(whiteFill);
      this.flasher.visible = false;
    }
    if (mode === 1) {
      this.flashTotal = flashTotal - 1;
      this.flashAlpha = 75;
      this.flasher.visible = true;
    }
    if (mode === 2 && this.flashAlpha > 0) {
      this.flashAlpha -= 5;
      if (this.flashAlpha === 20 && this.flashTotal > 0) {
        --this.flashTotal;
        this.flashAlpha = 65;
      }
      this.flasher.alpha = this.flashAlpha * 0.01;
      if (this.flashAlpha === 0) {
        this.flasher.visible = false;
      }
    }
  }

  // ------------------------------------------------------------- input & lifecycle

  private keyEvents(): void {
    for (const binding of KEY_BINDINGS) {
      this.input[binding.slot] =
        binding.codes.some((code) => Key.isDown(code)) ||
        this.pointerInput.isDown(binding.slot);
    }
    this.pointerInput.releasePulses();
  }

  private destroyAll(): void {
    // AS3 nulled these typed slots between scenes so the display objects could
    // be collected; every one is rebuilt by the next sceneSetup before it reads
    const nil = RELEASED;
    this.x = 0;
    this.y = 0;
    if (this.levelRunner != null) {
      this.levelRunner.teardown();
      this.applyLevelSession(this.levelSession);
    }
    if (this.soundFX != null) {
      this.soundFX.kill();
    }
    while (this.numChildren > 0) {
      this.removeChildAt(0);
    }
    this.fader = nil;
    this.uBox = nil;
    this.cBox = nil;
    this.menu = nil;
    this.cs = nil;
    this.gui = nil;
    this.levelRunner = nil;
    this.levelSession = nil;
    this.flasher = nil;
    this.flashAlpha = 0;
    this.flashTotal = 0;
    this.menuRefreshPending = false;
  }

  private applyLevelSession(session: LevelSession): void {
    this.unlockedLevels = session.unlockedLevels;
    this.highScores = session.highScores;
    this.itemData = session.itemData;
    this.itemFound = session.itemFound;
    this.weaponData = session.weaponData;
    this.savedProgress = session.savedProgress;
    this.hyperArmour = session.hyperArmour;
    this.eventStack = session.eventStack;
  }

  private applySave(state: SaveState): void {
    this.unlockedLevels = state.unlockedLevels;
    this.completedLevels = state.completedLevels;
    this.acknowledgedLevels = state.acknowledgedLevels;
    this.highScores = state.highScores;
    this.itemData = state.itemData;
    this.itemFound = state.itemFound;
    this.weaponData = state.weaponData;
    this.savedProgress = state.savedProgress;
    this.soundState = state.soundState;
    this.hyperArmour = state.hyperArmour;
    this.cheatsOn = state.cheatsOn;
  }

  private wipeData(): void {
    this.applySave(newSaveState(this.content));
    const initialLevel = this.content.initialLevelIds[0];
    if (initialLevel == null) throw new Error("content has no initial level");
    this.levelSelected = initialLevel;
    this.level = initialLevel;
    this.savedWeapon = 0;
  }

  private saveData(): void {
    this.saveFile.write({
      schemaVersion: CONTENT_SCHEMA_VERSION,
      unlockedLevels: this.unlockedLevels,
      completedLevels: this.completedLevels,
      acknowledgedLevels: this.acknowledgedLevels,
      highScores: this.highScores,
      itemData: this.itemData,
      itemFound: this.itemFound,
      weaponData: this.weaponData,
      savedProgress: this.savedProgress,
      soundState: this.soundState,
      hyperArmour: this.hyperArmour,
      cheatsOn: this.cheatsOn,
    });
  }

  /** True once every world-map mission, the secret one included, has been cleared. */
  private get cheatsUnlocked(): boolean {
    if (this.cheatsSessionUnlock) return true;
    return this.content.worldMap.nodes.every((node) =>
      this.completedLevels.includes(node.id),
    );
  }

  private completeLevel(levelId: LevelId, score: number): void {
    this.content.level(levelId);
    let changed = false;
    if (score > (this.highScores[levelId] ?? 0)) {
      this.highScores[levelId] = score;
      changed = true;
    }
    if (!this.completedLevels.includes(levelId)) {
      this.completedLevels.push(levelId);
      changed = true;
    }
    for (const edge of this.content.outgoing(levelId)) {
      changed = this.unlockLevel(edge.to) || changed;
    }
    if (changed) this.saveData();
  }

  private unlockLevel(levelId: LevelId): boolean {
    this.content.level(levelId);
    if (this.unlockedLevels.includes(levelId)) return false;
    this.unlockedLevels.push(levelId);
    return true;
  }

  private loadData(): void {
    this.applySave(this.saveFile.read());
    const selectable = selectableMapNodes(
      this.content.worldMap,
      this.unlockedLevels,
    );
    const selected = selectable[selectable.length - 1]?.id;
    if (selected == null) throw new Error("save has no selectable level");
    this.levelSelected = selected;
    this.level = selected;
  }
}
